// ---------------------------------------------------------------------------
// Builders. Each returns the blocks for one card, in the order an editor reads
// them. Lengths are what the design's boxes hold without clipping.
// ---------------------------------------------------------------------------
const TITLE = 60;
const CARD_TITLE = 48;
const COPY = 240;
const LEDE = 400;
const BUTTON = 32;
const SHORT = 28;
function text(key, label, section, fallback, options = {}) {
    return {
        key,
        kind: options.kind ?? 'text',
        label,
        section,
        fallback,
        ...(options.item === undefined ? {} : { item: options.item }),
        ...(options.maxLength === undefined ? {} : { maxLength: options.maxLength }),
        ...(options.required === undefined ? {} : { required: options.required }),
    };
}
/**
 * Where a button goes.
 *
 * The fallback is the destination the page ships with — a real route on this
 * site, or blank where the destination does not exist yet. Blank renders the
 * button as an announced "coming soon" control rather than a dead link, so an
 * editor can never produce `href="#"` by clearing the field.
 */
function link(key, label, section, fallback, item) {
    return {
        key,
        kind: 'url',
        label: `${label} (blank = coming soon)`,
        section,
        fallback,
        ...(item === undefined ? {} : { item }),
        maxLength: 300,
    };
}
function visibility(key, label, section, item) {
    return { key, kind: 'toggle', label, section, fallback: 'shown', ...(item === undefined ? {} : { item }) };
}
function order(key, section, position, item) {
    return { key, kind: 'order', label: 'Position in the row', section, fallback: String(position), item };
}
function image(key, label, section, item) {
    // Empty: the photograph shipped with the design is drawn until an editor
    // picks an uploaded one. A non-empty fallback would be read as an asset id.
    return { key, kind: 'image', label, section, fallback: '', ...(item === undefined ? {} : { item }) };
}
/*
 * Slots 1–3 kept their keys from the first design. Slot 2 was "Consultations"
 * and is now "Find Businesses"; slot 6 was "Health Plans" and is now "Book
 * Consultations", which inherits the old Consultations image slot. Nothing had
 * been stored against either on beta when this changed (checked 2026-10-06).
 */
const EXPLORE = [
    {
        n: 1,
        title: 'Medicines',
        copy: 'Order in seconds, delivered in minutes.',
        cta: 'Go to Shop',
        link: '/search',
        imageKey: 'explore.medicines.image',
    },
    {
        n: 2,
        title: 'Find Businesses',
        copy: 'Look for local pharmacies, and other providers.',
        cta: 'Discover Providers',
        link: '/search',
        imageKey: 'explore.find-businesses.image',
    },
    {
        n: 3,
        title: 'Blood Tests',
        copy: 'Health checks with sample collection at home.',
        cta: 'Buy Test Kits',
        link: '/search?q=blood%20test',
        imageKey: 'explore.blood-tests.image',
    },
    {
        n: 4,
        title: 'Nutrition & Special Diet',
        copy: 'All the health essentials whenever you need them.',
        cta: 'Go to Shop',
        link: '/search?q=vitamins',
        imageKey: 'explore.nutrition.image',
    },
    {
        n: 5,
        title: 'Care Homes',
        copy: 'Find trusted care homes near you.',
        cta: 'Find care homes',
        link: '',
        imageKey: 'explore.care-homes.image',
    },
    {
        n: 6,
        title: 'Book Consultations',
        copy: 'Personalised care services that fit your health goals.',
        cta: 'Book Consultation',
        link: '/search?kind=SERVICE',
        imageKey: 'explore.consultations.image',
    },
];
function exploreCard(seed) {
    const s = 'Explore';
    const item = `Card ${seed.n} — ${seed.title}`;
    const k = `explore.card${seed.n}`;
    return [
        text(`${k}-title`, 'Title', s, seed.title, { item, maxLength: CARD_TITLE, required: true }),
        text(`${k}-copy`, 'Copy', s, seed.copy, { item, maxLength: COPY }),
        text(`${k}-cta`, 'Button', s, seed.cta, { item, maxLength: BUTTON, required: true }),
        link(`${k}-link`, 'Button link', s, seed.link, item),
        image(seed.imageKey, 'Image', s, item),
        visibility(`${k}-visible`, 'Shown on the page', s, item),
        order(`${k}-order`, s, seed.n, item),
    ];
}
// --- Pharmacy OS ------------------------------------------------------------
const OS_TABS = [
    ['dashboard', 'Pharmacy Dashboard'],
    ['demand', 'Demand Intelligence'],
    ['compliance', 'Compliance'],
    ['data', 'Pharmacy Data'],
    ['operations-ai', 'Operations AI'],
    ['emr', 'Pharmacy EMR'],
    ['delivery', 'Pharmacy Delivery'],
];
function osTab([slug, label], index) {
    const s = 'Pharmacy OS';
    const item = `Tab ${index + 1} — ${label}`;
    return [
        text(`os.${slug}.label`, 'Tab label', s, label, { item, maxLength: SHORT, required: true }),
        image(`os.${slug}.image`, slug === 'delivery'
            ? 'Screen (blank = the live animated route map)'
            : 'Screen (blank = the shipped screenshot)', s, item),
        visibility(`os.${slug}.visible`, 'Shown on the page', s, item),
        order(`os.${slug}.order`, s, index + 1, item),
    ];
}
// --- Solutions --------------------------------------------------------------
const SOLUTIONS = [
    [
        'pharmacies',
        'Pharmacies',
        'Prescriptions, dispensing, consultations, products and delivery — all connected in one platform.',
    ],
    ['clinics', 'Clinics & GPs', 'Bookings, consultations, practitioners and patient services made simple.'],
    [
        'beauty',
        'Beauty & Aesthetics',
        'Manage treatments, practitioners, appointments and client experiences in one place.',
    ],
    [
        'gyms',
        'Gyms & Fitness',
        'Memberships, classes, personal trainers, bookings and programmes — all connected.',
    ],
    [
        'dentists',
        'Dentists',
        'Treatments, consultations, appointments and patient enquiries in one connected system.',
    ],
    [
        'wellness',
        'Wellness Centres',
        'Manage treatments, packages, memberships, health questionnaires and bookings.',
    ],
    [
        'physiotherapy',
        'Physiotherapy',
        'Manage appointments, treatment plans, practitioners, follow-ups and virtual consultations.',
    ],
    [
        'private-hospitals',
        'Private Hospitals',
        'Connect patients with consultants, departments, procedures, diagnostics and appointments.',
    ],
    [
        'nutrition-weight',
        'Nutrition & Weight',
        'Consultations, programmes, progress tracking, appointments and ongoing support.',
    ],
    [
        'mental-wellbeing',
        'Mental Wellbeing',
        'Secure consultations, appointments, practitioner profiles and ongoing client support.',
    ],
    [
        'care-providers',
        'Care Providers',
        'Coordinate services, staff, appointments and communication while keeping care connected.',
    ],
    [
        'brands',
        'Health & Wellness Brands',
        'Showcase health, beauty and wellbeing products and reach customers across the Just Treated network.',
    ],
];
function solutionCard([slug, title, copy], index) {
    const s = 'Solutions';
    const item = `Card ${index + 1} — ${title}`;
    return [
        text(`solutions.${slug}.title`, 'Title', s, title, { item, maxLength: CARD_TITLE, required: true }),
        text(`solutions.${slug}.copy`, 'Copy', s, copy, { item, maxLength: COPY }),
        image(`solutions.${slug}.image`, 'Photograph', s, item),
        visibility(`solutions.${slug}.visible`, 'Shown on the page', s, item),
        order(`solutions.${slug}.order`, s, index + 1, item),
    ];
}
// --- Partner Portal ---------------------------------------------------------
const PORTAL = [
    ['sales', 'Track your latest sales', 'Monitor orders and average order value to adjust pricing.'],
    [
        'conversion',
        'Track customer conversion',
        'Check and improve how many customers who view your listings go on to place an order.',
    ],
    [
        'revenue',
        'Explore revenue trends',
        'Spot high-performing items and areas for improvement to help future planning.',
    ],
    [
        'promotions',
        'Target key customers with promotions',
        'Run smart promotions to target the most valuable customers for your business.',
    ],
    [
        'forecast',
        'Forecast demand & stock',
        'Forecast demand, optimise stock and stay ahead of supply shortages.',
    ],
];
function portalCard([slug, title, copy], index) {
    const s = 'Partner Portal';
    const item = `Card ${index + 1} — ${title}`;
    return [
        text(`portal.${slug}.title`, 'Title', s, title, { item, maxLength: CARD_TITLE, required: true }),
        text(`portal.${slug}.copy`, 'Copy', s, copy, { item, maxLength: COPY }),
        // The first four keep their original labels' keys.
        image(`portal.${slug}.image`, 'Image', s, item),
        visibility(`portal.${slug}.visible`, 'Shown on the page', s, item),
        order(`portal.${slug}.order`, s, index + 1, item),
    ];
}
const GROW = [
    {
        slug: 'pharmacy',
        kicker: 'Grow as a Just Treated pharmacy',
        title: 'List your pharmacy',
        copy: 'Reach thousands of local patients with dispensing, delivery and compliance handled in one platform. When demand is high, you can earn even more.',
        cta: 'Get started',
        link: '/partners/',
    },
    {
        slug: 'clinician',
        kicker: 'Earn with every consultation',
        title: 'Join as a clinician',
        copy: 'Work with Just Treated and see more patients. Flexible online consultations that fit around your existing practice.',
        cta: 'Apply now',
        link: '/partners/',
    },
    {
        slug: 'courier',
        kicker: 'Deliver health, on your schedule',
        title: 'Become a delivery partner',
        copy: 'Earn more with flexible healthcare delivery opportunities. Choose your hours, get paid weekly.',
        cta: 'Register as a courier',
        // Couriers sign up in the JT Delivery app, which has no public listing yet.
        link: '',
    },
    {
        slug: 'suppliers',
        kicker: 'Supply a growing network',
        title: 'Suppliers & procurement',
        copy: 'Supply medicines and healthcare products to a growing national network of pharmacies and clinics.',
        cta: 'Partner with us',
        link: '/partners/',
    },
];
function growRow(seed, index) {
    const s = 'Grow';
    const item = `Row ${index + 1} — ${seed.title}`;
    const k = `grow.${seed.slug}`;
    return [
        text(`${k}.kicker`, 'Kicker', s, seed.kicker, { item, maxLength: TITLE }),
        text(`${k}.title`, 'Title', s, seed.title, { item, maxLength: CARD_TITLE, required: true }),
        text(`${k}.copy`, 'Copy', s, seed.copy, { item, maxLength: COPY }),
        text(`${k}.cta`, 'Button', s, seed.cta, { item, maxLength: BUTTON, required: true }),
        link(`${k}.link`, 'Button link', s, seed.link, item),
        image(`${k}.image`, 'Photograph', s, item),
        visibility(`${k}.visible`, 'Shown on the page', s, item),
        order(`${k}.order`, s, index + 1, item),
    ];
}
// --- Download ---------------------------------------------------------------
function downloadTab(slug, seed) {
    const s = 'Download';
    const item = `${seed.tab} tab`;
    return [
        text(`download.${slug}.tab`, 'Tab label', s, seed.tab, { item, maxLength: SHORT, required: true }),
        text(`download.${slug}.title`, 'Title', s, seed.title, { item, maxLength: TITLE, required: true }),
        text(`download.${slug}.copy`, 'Copy', s, seed.copy, { item, maxLength: COPY }),
        text(`download.${slug}.cta`, 'Button', s, seed.cta, { item, maxLength: BUTTON, required: true }),
        link(`download.${slug}.link`, 'Button link', s, seed.link, item),
        image(seed.imageKey, 'Photograph', s, item),
    ];
}
// --- News -------------------------------------------------------------------
const NEWS = [
    ['app-update', 'Product', 'New app update: faster, simpler and more reliable', '1 Sep 2026'],
    ['communities', 'Community', 'Supporting healthier communities across the UK', '28 Aug 2026'],
    ['penny', 'Rewards', 'Earn PENNY on every order with our new loyalty scheme', '21 Aug 2026'],
];
function newsItem([slug, tag, title, date], index) {
    const s = 'News';
    const item = `Article ${index + 1}`;
    return [
        text(`news.${slug}.tag`, 'Category', s, tag, { item, maxLength: SHORT }),
        text(`news.${slug}.title`, 'Headline', s, title, { item, maxLength: 120, required: true }),
        text(`news.${slug}.date`, 'Date', s, date, { item, maxLength: SHORT }),
        link(`news.${slug}.link`, 'Article link', s, '', item),
        image(`news.${slug}.image`, 'Thumbnail', s, item),
        visibility(`news.${slug}.visible`, 'Shown on the page', s, item),
        order(`news.${slug}.order`, s, index + 1, item),
    ];
}
/** Ordered as the page reads, so the editor's list matches scrolling it. */
export const HOMEPAGE_BLOCKS = [
    // --- Header -------------------------------------------------------------
    {
        key: 'header.support',
        kind: 'text',
        label: 'Support link',
        section: 'Header',
        fallback: 'Support',
        maxLength: SHORT,
    },
    {
        key: 'header.signup',
        kind: 'text',
        label: 'Sign-up button',
        section: 'Header',
        fallback: 'Sign up',
        maxLength: SHORT,
    },
    // --- Hero ---------------------------------------------------------------
    {
        key: 'hero.title',
        kind: 'text',
        label: 'Headline',
        section: 'Hero',
        fallback: 'Healthcare, delivered to you',
        // The headline sits on one line at every width; much longer and it cannot.
        maxLength: 40,
        required: true,
    },
    {
        key: 'hero.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Hero',
        fallback: 'Medicines, consultations, lab tests and more — from trusted GPhC-registered providers near you. Whatever your health needs, we’re one tap away.',
        maxLength: LEDE,
    },
    {
        key: 'hero.cta-primary',
        kind: 'text',
        label: 'Primary button',
        section: 'Hero',
        fallback: 'Get Just Treated',
        maxLength: BUTTON,
        required: true,
    },
    // There is no consumer sign-up page on the website (customers join in the
    // app), so the button takes them to the app download section on this page.
    link('hero.cta-primary-link', 'Primary button link', 'Hero', '/#download'),
    {
        key: 'hero.cta-secondary',
        kind: 'text',
        label: 'Secondary button',
        section: 'Hero',
        fallback: 'Partner with us',
        maxLength: BUTTON,
        required: true,
    },
    // The partner portal: sign in, create a business account, start onboarding.
    link('hero.cta-secondary-link', 'Secondary button link', 'Hero', '/partners/'),
    {
        key: 'hero.image',
        kind: 'image',
        label: 'Hero photograph',
        section: 'Hero',
        fallback: '',
    },
    // --- Explore ------------------------------------------------------------
    visibility('explore.visible', 'Show this section', 'Explore'),
    {
        key: 'explore.title',
        kind: 'text',
        label: 'Heading',
        section: 'Explore',
        fallback: 'Explore Just Treated',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'explore.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Explore',
        fallback: 'Everything you need, in one connected platform. Shop products, book services, access healthcare and discover trusted businesses around you.',
        maxLength: LEDE,
    },
    ...EXPLORE.flatMap(exploreCard),
    // --- Pharmacy OS --------------------------------------------------------
    visibility('os.visible', 'Show this section', 'Pharmacy OS'),
    {
        key: 'os.title',
        kind: 'text',
        label: 'Heading',
        section: 'Pharmacy OS',
        fallback: 'Meet Pharmacy OS',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'os.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Pharmacy OS',
        fallback: 'Our next-generation platform for pharmacies — prescriptions, dispensing, inventory and compliance in one place. Fully integrated with the Just Treated network, so your pharmacy reaches thousands of new patients while staying effortlessly compliant.',
        maxLength: LEDE,
    },
    ...OS_TABS.flatMap(osTab),
    // --- Solutions ----------------------------------------------------------
    visibility('solutions.visible', 'Show this section', 'Solutions'),
    {
        key: 'solutions.title',
        kind: 'text',
        label: 'Heading',
        section: 'Solutions',
        fallback: 'Solutions for every business',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'solutions.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Solutions',
        fallback: 'Just Treated gives every health and wellbeing business the tools to connect with more people, deliver better experiences and grow — all through one seamless platform.',
        maxLength: LEDE,
    },
    text('solutions.cta', 'Link text', 'Solutions', 'Explore solutions for your business', { maxLength: 48 }),
    link('solutions.link', 'Link', 'Solutions', '/partners/'),
    ...SOLUTIONS.flatMap(solutionCard),
    // --- Partner Portal -----------------------------------------------------
    visibility('portal.visible', 'Show this section', 'Partner Portal'),
    {
        key: 'portal.eyebrow',
        kind: 'text',
        label: 'Eyebrow',
        section: 'Partner Portal',
        fallback: 'Partner Portal',
        maxLength: SHORT,
    },
    {
        key: 'portal.title',
        kind: 'text',
        label: 'Heading',
        section: 'Partner Portal',
        fallback: 'Your dashboard for insights and growth',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'portal.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Partner Portal',
        fallback: 'Get sales and performance insights and manage your business all in one place with the Partner Portal.',
        maxLength: LEDE,
    },
    ...PORTAL.flatMap(portalCard),
    // --- About / super-app --------------------------------------------------
    visibility('superapp.visible', 'Show this section', 'About us'),
    {
        key: 'superapp.eyebrow',
        kind: 'text',
        label: 'Eyebrow',
        section: 'About us',
        fallback: 'About us',
        maxLength: SHORT,
    },
    {
        key: 'superapp.title',
        kind: 'text',
        label: 'Heading',
        section: 'About us',
        fallback: 'Just Treated is the UK’s first healthcare super-app.',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'superapp.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'About us',
        fallback: 'We’re on a mission to make good healthcare effortless by bringing your care, medication, bookings, wellbeing and daily life together in one app, wherever you are.',
        maxLength: LEDE,
    },
    text('superapp.cta', 'Button', 'About us', 'Our mission', { maxLength: BUTTON, required: true }),
    link('superapp.link', 'Button link', 'About us', ''),
    // --- Grow ---------------------------------------------------------------
    visibility('grow.visible', 'Show this section', 'Grow'),
    {
        key: 'grow.title',
        kind: 'text',
        label: 'Heading',
        section: 'Grow',
        fallback: 'Grow with Just Treated',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'grow.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Grow',
        fallback: 'Join thousands of partners across the UK that grow with Just Treated. For pharmacies, clinicians, couriers and suppliers looking for new ways to boost revenue.',
        maxLength: LEDE,
    },
    ...GROW.flatMap(growRow),
    // --- Refer --------------------------------------------------------------
    visibility('refer.visible', 'Show this section (and the delivery-bag photograph under it)', 'Refer'),
    {
        key: 'refer.title',
        kind: 'text',
        label: 'Heading',
        section: 'Refer',
        fallback: 'Refer a business and earn £200',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'refer.lede',
        kind: 'richText',
        label: 'Sub-heading',
        section: 'Refer',
        fallback: 'Recommend Just Treated to businesses in your network and receive a reward of up to £200 once they register and start trading.',
        maxLength: LEDE,
    },
    text('refer.cta', 'Button', 'Refer', 'Refer a business', { maxLength: BUTTON, required: true }),
    // No business-referral programme exists on the platform yet.
    link('refer.link', 'Button link', 'Refer', ''),
    // --- Download -----------------------------------------------------------
    visibility('download.visible', 'Show this section', 'Download'),
    {
        key: 'download.title',
        kind: 'text',
        label: 'Heading',
        section: 'Download',
        fallback: 'Download our apps',
        maxLength: TITLE,
        required: true,
    },
    {
        key: 'download.lede',
        kind: 'text',
        label: 'Sub-heading',
        section: 'Download',
        fallback: 'Available for iOS and Android devices.',
        maxLength: COPY,
    },
    ...downloadTab('customers', {
        tab: 'Customers',
        title: 'The fast, affordable way to better health.',
        copy: 'Order medicines, book consultations and track deliveries — anytime, anywhere.',
        cta: 'Get Just Treated',
        // The store listings do not exist yet; see `appStoreListings`.
        link: '',
        imageKey: 'download.image',
    }),
    ...downloadTab('partners', {
        tab: 'Partners',
        title: 'Grow your business on Just Treated.',
        copy: 'Manage prescriptions, orders and deliveries — built for pharmacies, clinicians and couriers.',
        cta: 'Get the Partner app',
        link: '/partners/',
        imageKey: 'download.partners.image',
    }),
    // --- News ---------------------------------------------------------------
    visibility('news.visible', 'Show this section', 'News'),
    {
        key: 'news.title',
        kind: 'text',
        label: 'Heading',
        section: 'News',
        fallback: 'News and updates',
        maxLength: TITLE,
        required: true,
    },
    text('news.featured-tag', 'Category', 'News', 'Partnerships', {
        item: 'Featured article',
        maxLength: SHORT,
    }),
    text('news.featured-date', 'Date', 'News', '3 Sep 2026', { item: 'Featured article', maxLength: SHORT }),
    {
        key: 'news.featured-title',
        kind: 'text',
        label: 'Headline',
        section: 'News',
        fallback: 'Just Treated partners with leading UK labs for faster results',
        item: 'Featured article',
        maxLength: 120,
        required: true,
    },
    link('news.featured-link', 'Article link', 'News', '', 'Featured article'),
    {
        key: 'news.featured-image',
        kind: 'image',
        label: 'Image',
        section: 'News',
        fallback: '',
        item: 'Featured article',
    },
    ...NEWS.flatMap(newsItem),
    // --- Footer -------------------------------------------------------------
    {
        key: 'footer.blurb',
        kind: 'richText',
        label: 'Description',
        section: 'Footer',
        fallback: 'Your trusted healthcare platform for medicines, consultations, lab tests and more.',
        maxLength: COPY,
    },
    // --- Images -----------------------------------------------------------
    {
        key: 'refer.image',
        kind: 'image',
        label: 'Delivery-bag photograph (full-bleed, under the referral band)',
        section: 'Refer',
        fallback: '',
    },
    // --- Menu and footer chrome -------------------------------------------
    {
        key: 'menu.title',
        kind: 'text',
        label: 'Menu title',
        section: 'Menu',
        fallback: 'Everything on Just Treated',
    },
    {
        key: 'menu.group-services',
        kind: 'text',
        label: 'Group: Services',
        section: 'Menu',
        fallback: 'Services',
    },
    {
        key: 'menu.group-business',
        kind: 'text',
        label: 'Group: Business',
        section: 'Menu',
        fallback: 'Business',
    },
    {
        key: 'menu.group-pharmacy',
        kind: 'text',
        label: 'Group: Pharmacy',
        section: 'Menu',
        fallback: 'Pharmacy',
    },
    { key: 'menu.group-company', kind: 'text', label: 'Group: Company', section: 'Menu', fallback: 'Company' },
    { key: 'menu.group-help', kind: 'text', label: 'Group: Help', section: 'Menu', fallback: 'Help' },
    { key: 'menu.group-shop', kind: 'text', label: 'Group: Shop', section: 'Menu', fallback: 'Shop' },
    {
        key: 'menu.actions-title',
        kind: 'text',
        label: 'Action panel title',
        section: 'Menu',
        fallback: 'Grow with Just Treated',
    },
    {
        key: 'menu.actions-lede',
        kind: 'richText',
        label: 'Action panel copy',
        section: 'Menu',
        fallback: 'Bring your pharmacy, practice or fleet onto the platform.',
    },
    {
        key: 'footer.legal-line',
        kind: 'text',
        label: 'Copyright line',
        section: 'Footer',
        fallback: 'All rights reserved. GPhC-registered providers.',
    },
    // --- Navigation labels ------------------------------------------------------
    //
    // One per destination. `DestinationLink` reads the override and falls back to
    // the name declared on the route, so a label edited once changes it in the
    // mega menu, the footer and anywhere else that renders that destination.
    { key: 'nav.home', kind: 'text', label: 'Home', section: 'Navigation', fallback: 'Home' },
    { key: 'nav.logIn', kind: 'text', label: 'Log in', section: 'Navigation', fallback: 'Log in' },
    { key: 'nav.signUp', kind: 'text', label: 'Sign up', section: 'Navigation', fallback: 'Sign up' },
    {
        key: 'nav.account',
        kind: 'text',
        label: 'Your account',
        section: 'Navigation',
        fallback: 'Your account',
    },
    { key: 'nav.search', kind: 'text', label: 'Search', section: 'Navigation', fallback: 'Search' },
    { key: 'nav.offers', kind: 'text', label: 'Offers', section: 'Navigation', fallback: 'Offers' },
    { key: 'nav.support', kind: 'text', label: 'Support', section: 'Navigation', fallback: 'Support' },
    { key: 'nav.basket', kind: 'text', label: 'Basket', section: 'Navigation', fallback: 'Basket' },
    { key: 'nav.nearby', kind: 'text', label: 'Nearby', section: 'Navigation', fallback: 'Nearby' },
    {
        key: 'nav.prescriptionMedicines',
        kind: 'text',
        label: 'Prescription Medicines',
        section: 'Navigation',
        fallback: 'Prescription Medicines',
    },
    {
        key: 'nav.overTheCounter',
        kind: 'text',
        label: 'Over the Counter',
        section: 'Navigation',
        fallback: 'Over the Counter',
    },
    {
        key: 'nav.vitamins',
        kind: 'text',
        label: 'Vitamins & Supplements',
        section: 'Navigation',
        fallback: 'Vitamins & Supplements',
    },
    {
        key: 'nav.healthAndBaby',
        kind: 'text',
        label: 'Health & Baby',
        section: 'Navigation',
        fallback: 'Health & Baby',
    },
    {
        key: 'nav.healthDevices',
        kind: 'text',
        label: 'Health & Devices',
        section: 'Navigation',
        fallback: 'Health & Devices',
    },
    {
        key: 'nav.personalCare',
        kind: 'text',
        label: 'Personal Care',
        section: 'Navigation',
        fallback: 'Personal Care',
    },
    { key: 'nav.petHealth', kind: 'text', label: 'Pet Health', section: 'Navigation', fallback: 'Pet Health' },
    {
        key: 'nav.medicinesPharmacy',
        kind: 'text',
        label: 'Medicines & Pharmacy',
        section: 'Navigation',
        fallback: 'Medicines & Pharmacy',
    },
    {
        key: 'nav.pharmacyMedicines',
        kind: 'text',
        label: 'Pharmacy Medicines',
        section: 'Navigation',
        fallback: 'Pharmacy Medicines',
    },
    {
        key: 'nav.everydayHealth',
        kind: 'text',
        label: 'Everyday Health',
        section: 'Navigation',
        fallback: 'Everyday Health',
    },
    {
        key: 'nav.consultations',
        kind: 'text',
        label: 'Online Consultations',
        section: 'Navigation',
        fallback: 'Online Consultations',
    },
    {
        key: 'nav.labTests',
        kind: 'text',
        label: 'Lab Tests at Home',
        section: 'Navigation',
        fallback: 'Lab Tests at Home',
    },
    {
        key: 'nav.healthChecks',
        kind: 'text',
        label: 'Health Checks',
        section: 'Navigation',
        fallback: 'Health Checks',
    },
    {
        key: 'nav.prescriptionService',
        kind: 'text',
        label: 'Prescription Service',
        section: 'Navigation',
        fallback: 'Prescription Service',
    },
    {
        key: 'nav.repeatOrders',
        kind: 'text',
        label: 'Repeat Orders',
        section: 'Navigation',
        fallback: 'Repeat Orders',
    },
    {
        key: 'nav.healthPlans',
        kind: 'text',
        label: 'Health Plans & Memberships',
        section: 'Navigation',
        fallback: 'Health Plans & Memberships',
    },
    {
        key: 'nav.healthSupport',
        kind: 'text',
        label: 'Health Support',
        section: 'Navigation',
        fallback: 'Health Support',
    },
    {
        key: 'nav.appointments',
        kind: 'text',
        label: 'Appointments',
        section: 'Navigation',
        fallback: 'Appointments',
    },
    {
        key: 'nav.pharmacyServices',
        kind: 'text',
        label: 'Pharmacy Services',
        section: 'Navigation',
        fallback: 'Pharmacy Services',
    },
    {
        key: 'nav.careServices',
        kind: 'text',
        label: 'Care Services',
        section: 'Navigation',
        fallback: 'Care Services',
    },
    {
        key: 'nav.beautyWellness',
        kind: 'text',
        label: 'Beauty & Wellness',
        section: 'Navigation',
        fallback: 'Beauty & Wellness',
    },
    { key: 'nav.delivery', kind: 'text', label: 'Delivery', section: 'Navigation', fallback: 'Delivery' },
    { key: 'nav.collection', kind: 'text', label: 'Collection', section: 'Navigation', fallback: 'Collection' },
    {
        key: 'nav.tracking',
        kind: 'text',
        label: 'Delivery Tracking',
        section: 'Navigation',
        fallback: 'Delivery Tracking',
    },
    {
        key: 'nav.send',
        kind: 'text',
        label: 'Just Treated Send',
        section: 'Navigation',
        fallback: 'Just Treated Send',
    },
    {
        key: 'nav.listPharmacy',
        kind: 'text',
        label: 'List your pharmacy',
        section: 'Navigation',
        fallback: 'List your pharmacy',
    },
    {
        key: 'nav.listHealthcareBusiness',
        kind: 'text',
        label: 'List your healthcare business',
        section: 'Navigation',
        fallback: 'List your healthcare business',
    },
    {
        key: 'nav.joinClinician',
        kind: 'text',
        label: 'Join as a clinician',
        section: 'Navigation',
        fallback: 'Join as a clinician',
    },
    {
        key: 'nav.careWellnessProviders',
        kind: 'text',
        label: 'Care & wellness providers',
        section: 'Navigation',
        fallback: 'Care & wellness providers',
    },
    {
        key: 'nav.deliveryPartners',
        kind: 'text',
        label: 'Delivery partners',
        section: 'Navigation',
        fallback: 'Delivery partners',
    },
    {
        key: 'nav.suppliers',
        kind: 'text',
        label: 'Suppliers & procurement',
        section: 'Navigation',
        fallback: 'Suppliers & procurement',
    },
    {
        key: 'nav.enterprise',
        kind: 'text',
        label: 'Enterprise / HQ',
        section: 'Navigation',
        fallback: 'Enterprise / HQ',
    },
    {
        key: 'nav.partnerPortal',
        kind: 'text',
        label: 'Partner portal',
        section: 'Navigation',
        fallback: 'Partner portal',
    },
    {
        key: 'nav.apiIntegrations',
        kind: 'text',
        label: 'API & integrations',
        section: 'Navigation',
        fallback: 'API & integrations',
    },
    {
        key: 'nav.pharmacyOs',
        kind: 'text',
        label: 'Pharmacy OS',
        section: 'Navigation',
        fallback: 'Pharmacy OS',
    },
    {
        key: 'nav.osPrescriptions',
        kind: 'text',
        label: 'Prescriptions',
        section: 'Navigation',
        fallback: 'Prescriptions',
    },
    {
        key: 'nav.osDispensing',
        kind: 'text',
        label: 'Dispensing',
        section: 'Navigation',
        fallback: 'Dispensing',
    },
    {
        key: 'nav.osInventory',
        kind: 'text',
        label: 'Inventory & Stock',
        section: 'Navigation',
        fallback: 'Inventory & Stock',
    },
    {
        key: 'nav.osPickPack',
        kind: 'text',
        label: 'Barcode, Pick & Pack',
        section: 'Navigation',
        fallback: 'Barcode, Pick & Pack',
    },
    { key: 'nav.osNms', kind: 'text', label: 'NMS', section: 'Navigation', fallback: 'NMS' },
    {
        key: 'nav.osPatient360',
        kind: 'text',
        label: 'Patient 360',
        section: 'Navigation',
        fallback: 'Patient 360',
    },
    {
        key: 'nav.osPmr',
        kind: 'text',
        label: 'PMR & Integrations',
        section: 'Navigation',
        fallback: 'PMR & Integrations',
    },
    {
        key: 'nav.osCompliance',
        kind: 'text',
        label: 'SOP & Compliance',
        section: 'Navigation',
        fallback: 'SOP & Compliance',
    },
    {
        key: 'nav.osProcurement',
        kind: 'text',
        label: 'Procurement',
        section: 'Navigation',
        fallback: 'Procurement',
    },
    {
        key: 'nav.osReports',
        kind: 'text',
        label: 'Reports & Insights',
        section: 'Navigation',
        fallback: 'Reports & Insights',
    },
    { key: 'nav.penny', kind: 'text', label: 'PENNY', section: 'Navigation', fallback: 'PENNY' },
    {
        key: 'nav.news',
        kind: 'text',
        label: 'News and updates',
        section: 'Navigation',
        fallback: 'News and updates',
    },
    { key: 'nav.about', kind: 'text', label: 'About Us', section: 'Navigation', fallback: 'About Us' },
    { key: 'nav.careers', kind: 'text', label: 'Careers', section: 'Navigation', fallback: 'Careers' },
    {
        key: 'nav.partnersWithUs',
        kind: 'text',
        label: 'Partners With Us',
        section: 'Navigation',
        fallback: 'Partners With Us',
    },
    {
        key: 'nav.press',
        kind: 'text',
        label: 'Press & Media',
        section: 'Navigation',
        fallback: 'Press & Media',
    },
    { key: 'nav.contact', kind: 'text', label: 'Contact Us', section: 'Navigation', fallback: 'Contact Us' },
    {
        key: 'nav.helpCentre',
        kind: 'text',
        label: 'Help Centre',
        section: 'Navigation',
        fallback: 'Help Centre',
    },
    {
        key: 'nav.deliveryInformation',
        kind: 'text',
        label: 'Delivery Information',
        section: 'Navigation',
        fallback: 'Delivery Information',
    },
    {
        key: 'nav.returns',
        kind: 'text',
        label: 'Returns & Refunds',
        section: 'Navigation',
        fallback: 'Returns & Refunds',
    },
    {
        key: 'nav.privacy',
        kind: 'text',
        label: 'Privacy Policy',
        section: 'Navigation',
        fallback: 'Privacy Policy',
    },
    {
        key: 'nav.terms',
        kind: 'text',
        label: 'Terms & Conditions',
        section: 'Navigation',
        fallback: 'Terms & Conditions',
    },
    {
        key: 'nav.accessibility',
        kind: 'text',
        label: 'Accessibility',
        section: 'Navigation',
        fallback: 'Accessibility',
    },
    {
        key: 'nav.cookies',
        kind: 'text',
        label: 'Cookie settings',
        section: 'Navigation',
        fallback: 'Cookie settings',
    },
    { key: 'nav.security', kind: 'text', label: 'Security', section: 'Navigation', fallback: 'Security' },
];
/** The editor's left-hand grouping, in page order and without duplicates. */
export const HOMEPAGE_SECTIONS = [
    ...new Set(HOMEPAGE_BLOCKS.map((block) => block.section)),
];
//# sourceMappingURL=homepage-schema.js.map